import { CheckIcon, MinusCircleIcon, ProhibitIcon, XIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { ProgressBar } from '../progress/progress-bar'
import * as styles from './activity-progress.css.ts'

export type ActivityProgressStatus = 'initializing' | 'in-progress' | 'complete' | 'blocked' | 'error' | 'excluded'

export type ActivityProgressProps = {
  label: string
  status: ActivityProgressStatus
  current?: number
  total?: number
}

export const ActivityProgress: React.FC<ActivityProgressProps> = ({ label, status, current = 0, total = 0 }) => {
  const statusText = match(status)
    .with('initializing', () => `Initializing ${label}`)
    .with('in-progress', () => `${label} in progress`)
    .with('complete', () => `${label} complete`)
    .with('blocked', () => `${label} blocked`)
    .with('error', () => `${label} failed`)
    .with('excluded', () => `${label} excluded`)
    .exhaustive()

  const icon = match(status)
    .with('complete', () => <CheckIcon size={24} weight="light" />)
    .with('blocked', () => <ProhibitIcon size={24} weight="light" />)
    .with('error', () => <XIcon size={24} weight="light" />)
    .with('excluded', () => <MinusCircleIcon size={24} weight="light" />)
    .with('initializing', 'in-progress', () => null)
    .exhaustive()

  const percentage = match(status)
    .with('complete', () => 100)
    .with('blocked', 'error', 'excluded', () => 0)
    .with('initializing', 'in-progress', () => (total > 0 ? (current / total) * 100 : 0))
    .exhaustive()

  return (
    <div className={styles.container} role="status" aria-label={statusText}>
      <div className={styles.statusRow}>
        {icon && (
          <div className={styles.iconStyle({ status: status as 'complete' | 'blocked' | 'error' | 'excluded' })}>
            {icon}
          </div>
        )}
        <div className={styles.statusText({ status })}>{statusText}</div>
      </div>

      <ProgressBar
        fill="gradient"
        radius="full"
        size="small"
        trackColor="alternate"
        value={percentage / 100}
        aria-label={`${label} progress`}
      />
    </div>
  )
}
